import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaPhoneAlt, FaSearch, FaMapMarkerAlt, FaPlus, FaClock, FaBuilding
} from 'react-icons/fa';
import './SheriffOffices.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const DispatchCenters = () => {
  const [centers, setCenters] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    counties: [],
    agencyTypes: []
  });
  const [filters, setFilters] = useState({
    search: '',
    county: '',
    agencyType: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchFilterOptions();
    fetchCenters();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCenters();
    }, 300);

    return () => clearTimeout(timer);
  }, [filters]);

  const fetchFilterOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/dispatch-centers/filters/options`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setFilterOptions(data.data);
      }
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  const fetchCenters = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Build query string
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.county) params.append('county', filters.county);
      if (filters.agencyType) params.append('agencyType', filters.agencyType);

      const response = await fetch(`${API_URL}/dispatch-centers?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setCenters(data.data);
        setError('');
      } else {
        setError('Failed to load dispatch centers');
      }
    } catch (err) {
      console.error('Error fetching dispatch centers:', err);
      setError('Failed to load dispatch centers');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      county: '',
      agencyType: ''
    });
  };

  const handleCenterClick = (centerId) => {
    navigate(`/address-book/dispatch-centers/${centerId}`);
  };

  const getAgencyTypeBadge = (agencyType) => {
    if (!agencyType) return null;

    const colors = {
      'Sheriff': '#2c3e50',
      'Police': '#3498db',
      'Consolidated': '#9b59b6',
      'State': '#16a085',
      'Federal': '#c0392b',
      'Tribal': '#d68910'
    };

    const color = colors[agencyType] || '#7f8c8d';

    return (
      <span className="county-badge" style={{ background: color }}>
        {agencyType}
      </span>
    );
  };

  if (loading && centers.length === 0) {
    return (
      <div className="sheriff-offices-container">
        <div className="loading">Loading dispatch centers...</div>
      </div>
    );
  }

  return (
    <div className="sheriff-offices-container">
      {/* Header */}
      <div className="sheriff-offices-header">
        <div className="header-left">
          <FaPhoneAlt size={32} style={{ color: '#e74c3c' }} />
          <div>
            <h1>Montana Dispatch Centers</h1>
            <p className="subtitle">{centers.length} dispatch centers found</p>
          </div>
        </div>
        <button className="btn-add-office" style={{ background: '#e74c3c' }} onClick={() => navigate('/address-book/dispatch-centers/new')}>
          <FaPlus /> Add Dispatch Center
        </button>
      </div>

      {/* Filters */}
      <div className="sheriff-offices-filters">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by facility name, county, or city..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-row">
          <div className="filter-group">
            <label>County</label>
            <select
              value={filters.county}
              onChange={(e) => handleFilterChange('county', e.target.value)}
              className="filter-select"
            >
              <option value="">All Counties</option>
              {filterOptions.counties.map(county => (
                <option key={county} value={county}>{county}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Agency Type</label>
            <select
              value={filters.agencyType}
              onChange={(e) => handleFilterChange('agencyType', e.target.value)}
              className="filter-select"
            >
              <option value="">All Agency Types</option>
              {filterOptions.agencyTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {(filters.search || filters.county || filters.agencyType) && (
            <button className="btn-clear-filters" onClick={clearFilters}>
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Dispatch Centers List */}
      <div className="offices-list">
        {centers.length === 0 ? (
          <div className="no-results">
            <FaPhoneAlt size={48} style={{ color: '#e74c3c' }} />
            <p>No dispatch centers found matching your criteria</p>
          </div>
        ) : (
          centers.map(center => (
            <div
              key={center.id}
              className="office-card"
              onClick={() => handleCenterClick(center.id)}
              style={{ borderColor: '#f0f0f0' }}
            >
              <div className="office-header">
                <div className="office-title">
                  <FaPhoneAlt className="office-icon" style={{ color: '#e74c3c' }} />
                  <h3>{center.facility_name || center.agency_jurisdiction || 'Unnamed Facility'}</h3>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  {center.county_location && (
                    <span className="county-badge">{center.county_location} County</span>
                  )}
                  {getAgencyTypeBadge(center.agency_type)}
                </div>
              </div>

              {/* ORI Number - PROMINENT */}
              {center.ori && (
                <div style={{
                  background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                  color: 'white',
                  padding: '0.875rem 1.25rem',
                  borderRadius: '10px',
                  marginBottom: '1rem',
                  boxShadow: '0 2px 8px rgba(231, 76, 60, 0.3)',
                  border: '2px solid rgba(255, 255, 255, 0.2)'
                }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: '600', opacity: '0.9', marginBottom: '0.25rem', letterSpacing: '1px' }}>
                    ORI NUMBER
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', letterSpacing: '2px', fontFamily: 'monospace' }}>
                    {center.ori}
                  </div>
                </div>
              )}

              {/* Main Phone (Non-Emergency) - PROMINENT */}
              {center.main_phone && (
                <div style={{
                  background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                  color: 'white',
                  padding: '0.75rem 1.25rem',
                  borderRadius: '10px',
                  marginBottom: '1rem',
                  boxShadow: '0 2px 6px rgba(52, 152, 219, 0.3)',
                  border: '2px solid rgba(255, 255, 255, 0.2)'
                }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: '600', opacity: '0.9', marginBottom: '0.25rem', letterSpacing: '1px' }}>
                    NON-EMERGENCY
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: '700', letterSpacing: '1px' }}>
                    {center.main_phone}
                  </div>
                </div>
              )}

              <div className="office-info">
                <div className="info-row">
                  {/* 24/7 Dispatch Phone */}
                  {center.dispatch_24hr && (
                    <div className="info-item">
                      <FaClock className="info-icon" style={{ color: '#27ae60' }} />
                      <span>
                        <strong style={{ color: '#27ae60' }}>24/7 Dispatch:</strong> {center.dispatch_24hr}
                      </span>
                    </div>
                  )}

                  {/* Location */}
                  {(center.city || center.county_location) && (
                    <div className="info-item">
                      <FaMapMarkerAlt className="info-icon" />
                      <span>
                        {center.city && `${center.city}, `}
                        {center.state || 'MT'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Consolidated Badge */}
                {center.is_consolidated && (
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 1rem',
                    background: '#9b59b6',
                    color: 'white',
                    borderRadius: '8px',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    marginTop: '0.5rem'
                  }}>
                    <FaBuilding />
                    CONSOLIDATED DISPATCH
                  </div>
                )}

                {/* Agency Jurisdiction */}
                {center.agency_jurisdiction && center.facility_name && (
                  <div className="address-detail">
                    Jurisdiction: {center.agency_jurisdiction}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default DispatchCenters;
